import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/AuthService';
import { useSite } from './SiteContext';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { resetSite } = useSite();
  const isLoggingOutRef = useRef(false);
  const [token, setAuthToken] = useState(() => {
    return !!sessionStorage.getItem('username');
  });

  const [userType, setUserType] = useState(() => sessionStorage.getItem('userType'));
  const [loggedInUserName, setLoggedInUserName] = useState(() => sessionStorage.getItem('username'));
  const [groupId, setGroupId] = useState(() => sessionStorage.getItem('groupId'));

  useEffect(() => {
    if (!token && !['/login', '/forgot-password', '/reset-password'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [token, navigate]);

  const login = (newUserType, username, newGroupId) => {
    isLoggingOutRef.current = false;
    sessionStorage.removeItem('authNotice');
    sessionStorage.setItem('userType', newUserType);
    sessionStorage.setItem('username', username);
    sessionStorage.setItem('groupId', newGroupId);

    setAuthToken(true); // just "logged in"
    setUserType(newUserType);
    setLoggedInUserName(username);
    setGroupId(newGroupId);
  };

  const logout = useCallback(async (options = {}) => {
    const { reason, message } = options;
    if (isLoggingOutRef.current) {
      return;
    }
    isLoggingOutRef.current = true;

    resetSite();

    try {
      await AuthService.logoutUser();
    } catch {
      /* ignore */
    }

    sessionStorage.clear();
    if (reason === 'expired') {
      sessionStorage.setItem(
        'authNotice',
        message || 'Your session has expired. Please log in again to continue.'
      );
    }

    const isProduction = process.env.NODE_ENV === 'production';
    const secureAttribute = isProduction ? 'Secure; ' : '';
    document.cookie = `AuthToken=; Max-Age=0; path=/; ${secureAttribute}SameSite=Strict;`;

    setAuthToken(false);
    setUserType(null);
    setLoggedInUserName(null);
    setGroupId(null);
    navigate('/login');
  }, [navigate, resetSite]);

  useEffect(() => {
    const onExpired = (event) => {
      logout({
        reason: event?.detail?.reason || 'expired',
        message: event?.detail?.message,
      });
    };
    window.addEventListener("auth-expired", onExpired);
    return () => window.removeEventListener("auth-expired", onExpired);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        token,              // "isAuthenticated" flag
        userType,
        loggedInUserName,
        groupId,
        login,
        logout,
        setUserType,
        setLoggedInUserName,
        setGroupId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
